import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalDialog,
  Option,
  Select,
  Stack,
  Typography,
} from "@mui/joy";
import { apiClient } from "../../lib/api";
import type { ProposalDetail, PublicProposal } from "../../types/proposals";
import { ResponsiveTable } from "../../components/common/ResponsiveTable";
import { isCalendarDatePast } from "../../lib/calendarDate";
import toast from "react-hot-toast";
interface History {
  version: string;
  can_undo: boolean;
  can_redo: boolean;
  history: {
    id: number;
    description: string;
    actor: string;
    created_at: string;
  }[];
  presets: { id: number; name: string }[];
}
const money = (value: unknown) =>
  Number(value || 0).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
  });
export default function ProposalBuilderTools({
  proposal,
  showTools = true,
  includesRoofing = proposal.service_configuration?.include_roofing !== false,
  savedAt,
  previewOpen,
  onClosePreview,
  beforeAction,
  onFieldChange,
}: {
  proposal: ProposalDetail;
  showTools?: boolean;
  includesRoofing?: boolean;
  savedAt: number;
  previewOpen: boolean;
  onClosePreview: () => void;
  beforeAction: () => Promise<void>;
  onFieldChange: (field: string, value: unknown) => void;
}) {
  const client = useQueryClient();
  const [panel, setPanel] = useState<
    "check" | "pricing" | "history" | "presets" | null
  >(null);
  const [width, setWidth] = useState(390);
  const [presetName, setPresetName] = useState("");
  const [preset, setPreset] = useState<number | null>(null);
  const [applyPages, setApplyPages] = useState(true);
  const [fields, setFields] = useState([
    "introduction_text",
    "scope_of_work",
    "terms_and_conditions",
  ]);
  const frame = useRef<HTMLIFrameElement>(null);
  const history = useQuery({
    enabled: showTools,
    queryKey: ["proposal-editor-history", String(proposal.id), savedAt],
    queryFn: async () =>
      (
        await apiClient.get<History>(
          `/api/proposals/${proposal.id}/editor-history/`,
        )
      ).data,
  });
  const preview = useQuery({
    queryKey: ["proposal-editor-preview", proposal.id, savedAt],
    enabled: previewOpen,
    queryFn: async () =>
      (
        await apiClient.get<PublicProposal>(
          `/api/proposals/public/${proposal.public_token}/?preview=1`,
        )
      ).data,
  });
  const sendPreview = () => {
    if (preview.data)
      frame.current?.contentWindow?.postMessage(
        { type: "APEXOS_PROPOSAL_PREVIEW", proposal: preview.data },
        window.location.origin,
      );
  };
  useEffect(() => {
    sendPreview();
    const ready = (event: MessageEvent) => {
      if (
        event.origin === window.location.origin &&
        event.source === frame.current?.contentWindow &&
        event.data?.type === "APEXOS_PROPOSAL_PREVIEW_READY"
      )
        sendPreview();
    };
    window.addEventListener("message", ready);
    return () => window.removeEventListener("message", ready);
  }, [preview.data, previewOpen]);
  const action = useMutation({
    meta: { skipEditorFlush: true },
    mutationFn: async ({
      endpoint,
      data,
    }: {
      endpoint: string;
      data: Record<string, unknown>;
    }) => {
      await beforeAction();
      // Undo/restore uses the version the user saw, rejecting edits from another session.
      return apiClient.post(`/api/proposals/${proposal.id}/${endpoint}/`, data);
    },
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ["proposal", String(proposal.id)] });
      client.invalidateQueries({
        queryKey: ["proposal-editor-history", String(proposal.id)],
      });
      toast.success("Proposal updated");
    },
    onError: (error: any) =>
      toast.error(
        error?.responseData?.detail ||
          error?.message ||
          "Could not update the proposal.",
      ),
  });
  const restore = (direction: string, revision?: number) =>
    action.mutate({
      endpoint: "editor-history-restore",
      data: { direction, revision, version: history.data?.version },
    });
  const checks = [
    ["Customer email", !!proposal.contact_details?.email],
    [
      "Roof quantity",
      Number(proposal.total_squares) > 0 ||
        proposal.tiers.every((tier) => tier.use_manual_pricing),
    ],
    [
      "Positive package pricing",
      proposal.tiers.length > 0 &&
        proposal.tiers.every((tier) => Number(tier.total_amount) > 0),
    ],
    ["Current expiration", !isCalendarDatePast(proposal.valid_until)],
    [
      "Visible quote and acceptance pages",
      proposal.pages.some(
        (page) => page.is_enabled && page.page_type === "quote_details",
      ) &&
        proposal.pages.some(
          (page) => page.is_enabled && page.page_type === "authorization",
        ),
    ],
  ] as const;
  const visibleChecks = includesRoofing ? checks : checks.filter(([label]) => label !== "Roof quantity" && label !== "Positive package pricing");
  const productChoices = [
    ...new Set(
      proposal.tiers.flatMap((tier) =>
        tier.line_items
          .filter((item) => item.product_details?.options?.length)
          .map((item) => item.product_details!.name),
      ),
    ),
  ];
  const context = proposal.measurement_context || {};
  return (
    <>
      {showTools && <Stack direction="row" gap={1} flexWrap="wrap" sx={{ my: 1 }}>
        <Button size="sm" variant="outlined" onClick={() => setPanel("check")}>
          Preflight
        </Button>
        {includesRoofing && <Button
          size="sm"
          variant="outlined"
          onClick={() => setPanel("pricing")}
        >
          Pricing worksheet
        </Button>}
        <Button
          size="sm"
          variant="outlined"
          onClick={() => setPanel("presets")}
        >
          Content and page presets
        </Button>
        <Button size="sm" variant="plain" onClick={() => setPanel("history")}>
          Edit history
        </Button>
        <Button
          size="sm"
          variant="plain"
          disabled={!history.data?.can_undo || action.isPending}
          onClick={() => restore("undo")}
        >
          Undo
        </Button>
        <Button
          size="sm"
          variant="plain"
          disabled={!history.data?.can_redo || action.isPending}
          onClick={() => restore("redo")}
        >
          Redo
        </Button>
      </Stack>}
      {showTools && history.isError && (
        <Alert
          color="warning"
          endDecorator={
            <Button onClick={() => history.refetch()}>Retry</Button>
          }
        >
          Editing history could not be loaded.
        </Alert>
      )}
      <Modal open={!!panel} onClose={() => setPanel(null)}>
        <ModalDialog
          sx={{
            width: "calc(100% - 32px)",
            maxWidth: 950,
            maxHeight: "90vh",
            overflow: "auto",
          }}
        >
          <Stack direction="row" justifyContent="space-between">
            <Typography level="h4">
              {panel === "check"
                ? "Preflight checklist"
                : panel === "pricing"
                  ? "Pricing worksheet"
                  : panel === "history"
                    ? "Saved editing history"
                    : "Reusable content and page presets"}
            </Typography>
            <Button variant="plain" onClick={() => setPanel(null)}>
              Close
            </Button>
          </Stack>
          {panel === "check" && (
            <Stack spacing={1}>
              {visibleChecks.map(([label, ready]) => (
                <Chip key={label} color={ready ? "success" : "warning"}>
                  {ready ? "Ready" : "Review"} · {label}
                </Chip>
              ))}
              {includesRoofing ? <Typography>
                Customer choices at acceptance:{" "}
                {productChoices.join(", ") || "No required product options"}
              </Typography> : <Alert>Review quantities, prices and customer options in Services, then check the customer preview before sending.</Alert>}
              <Typography level="body-sm">
                Checks reflect the saved proposal. Preview and Send first wait
                for your current edits to save.
              </Typography>
            </Stack>
          )}
          {panel === "pricing" && includesRoofing && (
            <>
              <Typography>
                Measurement source:{" "}
                {String(context.source || "Existing / manual entry")} · Waste:{" "}
                {Number(context.waste_percent || 0)}%
              </Typography>
              {!!context.breakdown && (
                <Typography>{String(context.breakdown)}</Typography>
              )}
              <Typography>
                Measured squares:{" "}
                {Number(
                  context.measured_squares || proposal.total_squares || 0,
                )}{" "}
                · Current pricing quantity:{" "}
                {Number(proposal.total_squares || 0)}
              </Typography>
              <FormControl>
                <FormLabel>Adjusted pricing quantity (squares)</FormLabel>
                <Input
                  type="number"
                  defaultValue={proposal.total_squares || ""}
                  onBlur={(event) => {
                    const value = Number(event.target.value);
                    if (Number.isFinite(value) && value >= 0)
                      onFieldChange("total_squares", value);
                  }}
                />
              </FormControl>
              <Typography level="body-sm">
                Tax:{" "}
                {proposal.tax_enabled ? `${proposal.tax_rate}%` : "Disabled"}.
                Manual packages retain their entered subtotal when quantities
                change.
              </Typography>
              <ResponsiveTable
                rows={proposal.tiers}
                getRowKey={(tier) => tier.id}
                columns={[
                  {
                    key: "name",
                    header: "Package",
                    mobileVariant: "header",
                    render: (tier) => tier.name,
                  },
                  {
                    key: "basis",
                    header: "Pricing basis",
                    render: (tier) =>
                      tier.use_manual_pricing
                        ? "Manual subtotal"
                        : tier.manufacturer_product_details?.price_per_square
                          ? `${money(tier.manufacturer_product_details.price_per_square)} / square`
                          : "Line item pricing",
                  },
                  {
                    key: "subtotal",
                    header: "Subtotal",
                    render: (tier) => money(tier.subtotal),
                  },
                  {
                    key: "tax",
                    header: "Tax",
                    render: (tier) => money(tier.tax_amount),
                  },
                  {
                    key: "total",
                    header: "Total",
                    render: (tier) => money(tier.total_amount),
                  },
                ]}
              />
            </>
          )}
          {panel === "history" && (
            <Stack spacing={1}>
              <Typography level="body-sm">
                Latest 100 saved edits. Undo and redo retain up to 50 steps.
                Restoring a version preserves a new history entry.
              </Typography>
              {!history.data?.history.length && (
                <Typography>
                  Edits made after history was introduced will appear here.
                </Typography>
              )}
              {history.data?.history.map((item) => (
                <Box
                  key={item.id}
                  sx={{
                    borderBottom: "1px solid",
                    borderColor: "divider",
                    py: 1,
                  }}
                >
                  <Typography>
                    {item.description} · {item.actor}
                  </Typography>
                  <Typography level="body-xs">
                    {new Date(item.created_at).toLocaleString()}
                  </Typography>
                  <Button
                    size="sm"
                    variant="soft"
                    disabled={action.isPending}
                    onClick={() => restore("restore", item.id)}
                  >
                    Restore this saved version
                  </Button>
                </Box>
              ))}
            </Stack>
          )}
          {panel === "presets" && (
            <Stack spacing={2}>
              <Typography>
                Save your introduction, scope, terms, and page arrangement for
                another job. Applying a preset keeps the target proposal’s
                photos and manufacturer assets.
              </Typography>
              <Input
                placeholder="Preset name"
                value={presetName}
                onChange={(event) => setPresetName(event.target.value)}
              />
              <Button
                loading={action.isPending}
                disabled={!presetName.trim()}
                onClick={() =>
                  action.mutate({
                    endpoint: "editor-save-preset",
                    data: { name: presetName },
                  })
                }
              >
                Save current content and layout
              </Button>
              <Select
                placeholder="Choose a saved preset"
                value={preset}
                onChange={(_, value) => setPreset(value)}
              >
                {history.data?.presets.map((item) => (
                  <Option key={item.id} value={item.id}>
                    {item.name}
                  </Option>
                ))}
              </Select>
              {[
                "introduction_text",
                "scope_of_work",
                "terms_and_conditions",
                "presentation_options",
              ].map((field) => (
                <Checkbox
                  key={field}
                  label={field.replaceAll("_", " ")}
                  checked={fields.includes(field)}
                  onChange={(event) =>
                    setFields((previous) =>
                      event.target.checked
                        ? [...previous, field]
                        : previous.filter((item) => item !== field),
                    )
                  }
                />
              ))}
              <Checkbox
                label="Page arrangement and visibility"
                checked={applyPages}
                onChange={(event) => setApplyPages(event.target.checked)}
              />
              <Alert color="warning">
                Applying replaces the selected content and page settings. You
                can undo the change.
              </Alert>
              <Button
                disabled={!preset || action.isPending}
                onClick={() =>
                  action.mutate({
                    endpoint: "editor-apply-preset",
                    data: { preset, fields, pages: applyPages },
                  })
                }
              >
                Apply selected content
              </Button>
            </Stack>
          )}
        </ModalDialog>
      </Modal>
      <Modal open={previewOpen} onClose={onClosePreview}>
        <ModalDialog
          sx={{ width: "calc(100% - 24px)", maxWidth: 1300, height: "95vh" }}
        >
          <Stack direction="row" gap={1} flexWrap="wrap" alignItems="center">
            <Typography level="title-lg" sx={{ flex: 1 }}>
              Customer preview · saved version
            </Typography>
            <Button variant="soft" onClick={() => setWidth(390)}>
              Mobile
            </Button>
            <Button variant="soft" onClick={() => setWidth(1120)}>
              Desktop
            </Button>
            <Button variant="plain" onClick={onClosePreview}>
              Close
            </Button>
          </Stack>
          <Typography level="body-xs">
            Previewing does not record a customer view. Signing is disabled.
          </Typography>
          {preview.isError ? (
            <Alert
              color="danger"
              endDecorator={
                <Button onClick={() => preview.refetch()}>Retry</Button>
              }
            >
              Preview could not be loaded.
            </Alert>
          ) : (
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                overflow: "auto",
                display: "flex",
                justifyContent: "center",
                bgcolor: "background.level2",
              }}
            >
              <iframe
                ref={frame}
                onLoad={sendPreview}
                title="Customer proposal preview"
                src={`${proposal.public_url}?preview=1&embedded=1`}
                style={{
                  width,
                  maxWidth: "100%",
                  height: "100%",
                  border: 0,
                  background: "#fff",
                }}
              />
            </Box>
          )}
        </ModalDialog>
      </Modal>
    </>
  );
}
